"use client";

import React, { useState } from "react";
import Link from "next/link";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOurTeamOpen, setIsOurTeamOpen] = useState(false);

  const teamMembers = [
    { name: "Zandoriel Lubbert", slug: "zandoriel-lubbert" },
    { name: "Sarah Mitchell", slug: "sarah-mitchell" },
    { name: "Robert J. Gartland", slug: "robert-j-gartland" },
    { name: "Jennifer Martinez", slug: "jennifer-martinez" }
  ];

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-accent/20 rounded-lg blur-lg group-hover:blur-xl transition-all duration-300" />
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="relative transition-transform duration-300 group-hover:scale-110 drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 4px 12px rgba(212, 175, 55, 0.5))' }}
              >
                <rect width="48" height="48" rx="8" fill="#D4AF37" />
                <path d="M14 34V14H18V30H28V34H14Z" fill="#1B365D" />
                <path d="M28 14H34L28 24L34 34H28L22 24L28 14Z" fill="#1B365D" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-primary font-serif font-bold text-2xl leading-tight tracking-tight">
                Lubbert Associates
              </span>
              <span className="text-accent text-sm font-bold tracking-wider uppercase">
                LLC
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link href="/homepage" className="text-gray-700 hover:text-accent transition-colors">
              Home
            </Link>
            <Link href="/about-us" className="text-gray-700 hover:text-accent transition-colors">
              About Us
            </Link>
            
            {/* Our Team Dropdown */}
            <div className="relative">
              <button
                className="text-gray-700 hover:text-accent transition-colors flex items-center"
                onMouseEnter={() => setIsOurTeamOpen(true)}
                onMouseLeave={() => setIsOurTeamOpen(false)}
              >
                Our Team
                <svg
                  className={`ml-1 w-4 h-4 transition-transform ${isOurTeamOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isOurTeamOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-64 bg-white shadow-lg rounded-md py-2 z-50"
                  onMouseEnter={() => setIsOurTeamOpen(true)}
                  onMouseLeave={() => setIsOurTeamOpen(false)}
                >
                  {teamMembers?.map((member) => (
                    <Link
                      key={member?.slug}
                      href={`/team-member-profile?member=${member?.slug}`}
                      className="block px-4 py-2 text-gray-700 hover:bg-accent/10 hover:text-accent transition-colors"
                    >
                      {member?.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link href="/practice-areas" className="text-gray-700 hover:text-accent transition-colors">
              Practice Areas
            </Link>
            <Link href="/faq-landing-page" className="text-gray-700 hover:text-accent transition-colors">
              FAQ
            </Link>
            <Link 
              href="/contact" 
              className="bg-accent text-primary px-6 py-2 rounded-md hover:bg-accent/90 transition-colors font-semibold"
            >
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4">
            <Link href="/homepage" className="block py-2 text-gray-700 hover:text-accent">
              Home
            </Link>
            <Link href="/about-us" className="block py-2 text-gray-700 hover:text-accent">
              About Us
            </Link>
            
            {/* Mobile Our Team Dropdown */}
            <div>
              <button
                className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-accent"
                onClick={() => setIsOurTeamOpen(!isOurTeamOpen)}
              >
                Our Team
                <svg
                  className={`w-4 h-4 transition-transform ${isOurTeamOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isOurTeamOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  {teamMembers?.map((member) => (
                    <Link
                      key={member?.slug}
                      href={`/team-member-profile?member=${member?.slug}`}
                      className="block py-2 text-gray-700 hover:text-accent"
                    >
                      {member?.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link href="/practice-areas" className="block py-2 text-gray-700 hover:text-accent">
              Practice Areas
            </Link>
            <Link href="/faq-landing-page" className="block py-2 text-gray-700 hover:text-accent">
              FAQ
            </Link>
            <Link 
              href="/contact" 
              className="block mt-4 bg-accent text-primary px-6 py-2 rounded-md text-center hover:bg-accent/90 font-semibold"
            >
              Schedule Consultation
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}